import { Component, OnInit, OnDestroy, Input, HostBinding } from '@angular/core';
import { SCNotification } from '@sc/types';
import { Subscription, firstValueFrom } from 'rxjs';
import { UserService } from '../../services/user/user.service';
import { NotificationsService } from '../../services/notifications/notifications.service';
import { ModalController } from '@ionic/angular';
import { NotificationsPage } from '../../modals/notifications/notifications.page';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { ResponsiveService } from '../../services/responsive/responsive.service';
import { OrganizationsService } from '../../services/organizations/organizations.service';

@Component({
  selector: 'sc-dashboard-layout',
  templateUrl: './dashboard-layout.component.html',
  styleUrls: ['./dashboard-layout.component.scss'],
})
export class DashboardLayoutComponent implements OnInit, OnDestroy {
  @Input() searchBar: string;
  @Input() accountPage: string;
  mobileNavOpen$ = this.responsiveService.mobileNavOpen$;
  mobileProfileNavOpen$ = this.responsiveService.mobileProfileNavOpen$;
  banners: SCNotification[] = [];
  subs: Array<Subscription> = [];

  constructor(
    private analyticsService: AnalyticsService,
    private modalController: ModalController,
    private notificationsService: NotificationsService,
    private responsiveService: ResponsiveService,
    private userService: UserService,
    private organizationsService: OrganizationsService
  ) {}

  @HostBinding('class.has-banner') hasBanner = false;

  ngOnInit() {
    this.setupNotifications();
  }

  async setupNotifications() {
    const user = await this.userService.activeUser$.nextExistingValue();
    if (user.guest) return;

    const readNotifications = this.notificationsService.readNotifications$.value;

    const sub = this.notificationsService.getNotifications('banner').subscribe((bannerNotifications) => {
      const result: SCNotification[] = [];

      const permanentBanners = bannerNotifications.filter((n) => n.permanent);
      result.push(...permanentBanners);

      const connectDescriptBanner = bannerNotifications.find(
        (b) => b.bannerOptions.buttonTitle === 'Connect To Descript' && !b.permanent
      );
      if (connectDescriptBanner && !this.organizationsService.dashboardOrg$.value.driveID) {
        result.push(connectDescriptBanner);
      }

      const regularBanner = bannerNotifications.find((notification) => {
        return (
          !notification.permanent &&
          !readNotifications?.find((v) => v.id == notification.id) &&
          !notification.version &&
          notification.bannerOptions.buttonTitle !== 'Connect To Descript'
        );
      });
      if (regularBanner) result.push(regularBanner);

      this.banners = result;
      this.hasBanner = this.banners.length > 0;
    });
    this.subs.push(sub);

    const modalNotifications = (await firstValueFrom(this.notificationsService.getNotifications('modal')))
      .filter((notification) => !readNotifications?.find((v) => v.id == notification.id))
      .sort((a, b) => {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        return +aDate - +bDate;
      });

    if (modalNotifications.length > 0 && !modalNotifications[0].isDescriptAnnouncement) {
      this.showNotificationsModal(modalNotifications);
    }
  }

  async showNotificationsModal(notifications: SCNotification[]) {
    if (this.organizationsService.dashboardOrg$.value.driveID)
      notifications = notifications.filter((v) => !v.isDescriptAnnouncement);

    const modal = await this.modalController.create({
      component: NotificationsPage,
      componentProps: {
        notifications,
      },
      cssClass: 'notifications-modal',
    });

    await modal.present();
    this.analyticsService.track('opened notifications modal', { notifications });
    return modal.onDidDismiss();
  }

  closeBanner(banner: SCNotification) {
    if (banner.permanent) return;
    this.notificationsService.setRead(banner.id);
    this.banners = this.banners.filter((b) => b.id !== banner.id);
    this.hasBanner = this.banners.length > 0;
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
